import React, { useMemo } from "react";
import { Star } from "lucide-react";
import { useProcessMonitoring } from "../../context/ProcessMonitoringContext";
import {
  formatProcessNumberWithIncident,
  normalizeCnjBaseDigits,
} from "../../utils/cnjProcessNumber.js";

/**
 * Formata número de processo judicial no padrão CNJ
 * Padrão: NNNNNNN-DD.AAAA.JTR.OOOO
 *
 * @param {string} processNumber - Número do processo (apenas dígitos)
 * @param {string} className - Classes CSS adicionais
 * @param {boolean} copyable - Se permite copiar ao clicar
 * @param {boolean} monitorable - Se exibe ⭐ para adicionar/remover do monitoramento
 * @returns {JSX.Element} Componente formatado
 */
const ProcessNumber = ({
  processNumber,
  className = "",
  copyable = false,
  monitorable = true,
  ...props
}) => {
  const { isMonitored, toggleMonitoring } = useProcessMonitoring();

  const normalized = useMemo(() => {
    if (!processNumber) return "";
    return normalizeCnjBaseDigits(processNumber);
  }, [processNumber]);

  const monitored = useMemo(() => {
    if (!monitorable || !normalized) return false;
    return isMonitored(normalized);
  }, [monitorable, normalized, isMonitored]);

  // Função para copiar para clipboard
  const handleCopy = async () => {
    if (!copyable) return;

    try {
      const formattedNumber = formatProcessNumberWithIncident(processNumber);
      await navigator.clipboard.writeText(formattedNumber);
    } catch (error) {
      // Falha ao copiar
    }
  };

  const handleToggleMonitoring = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!monitorable || !normalized) return;
    try {
      await toggleMonitoring(normalized);
    } catch (error) {
      // Erro já tratado no service
			console.error(error);
    }
  };

  const formattedNumber = formatProcessNumberWithIncident(processNumber);

  // Classes CSS base (define contraste forte no estado normal)
  const baseClasses = "font-mono text-sm text-gray-900";
  const copyableClasses = copyable
    ? "cursor-pointer px-2 py-1 rounded-lg border border-gray-300 bg-white shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-blue-700 transition-colors"
    : "";
  const finalClasses = `${baseClasses} ${copyableClasses}`.trim();

  return (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()} {...props}>
      <span
        className={finalClasses}
        onClick={handleCopy}
        title={copyable ? "Clique para copiar" : formattedNumber}
      >
        {formattedNumber}
      </span>

      {monitorable && normalized ? (
        <button
          type="button"
          onClick={handleToggleMonitoring}
          className={`p-1 rounded-lg border transition-colors shadow-sm ${
            monitored
              ? "border-yellow-300 bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
              : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-400"
          }`}
          title={monitored ? "Remover do monitoramento" : "Monitorar este processo"}
        >
          <Star className="w-4 h-4" fill={monitored ? "currentColor" : "none"} />
        </button>
      ) : null}
    </span>
  );
};

export default ProcessNumber;